import React, { useEffect, useState, useRef } from 'react';
import "./LenderDashboard.css";
import { getAllLenderRequests, isDraftRequest } from "../shared/services/LenderRequestService";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { SetFilterModule } from 'ag-grid-enterprise';
import RefreshIcon from "@material-ui/icons/Refresh";
import RequestDateRenderer from "../LenderRequests/List/AgGrid/RequestDateRenderer";
import EscalatedIndicatorRenderer from "../LenderRequests/List/AgGrid/EscalatedIndicatorRenderer";
import EditableCellRenderer from "../shared/components/AgGrid/EditableCellRenderer";
import { AssignReviewerModal } from "../LenderRequests/AssignReviewer/AssignReviewerModal";
import { fetchReviewers } from "../shared/services/LenderRequestService";
import { SubTypeTooltipRenderer } from "../shared/components/AgGrid/SubTypeTooltipRenderer";
import { REQUEST_STATUSES, CLOSED_STATUSES, IN_PROGRESS_STATUSES, OPEN_STATUSES } from "../shared/constants/RequestStatuses";

// Register the SetFilterModule
ModuleRegistry.registerModules([SetFilterModule]);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const LenderDashboard = () => {
  const [lastUpdated, setLastUpdated] = useState('Never');
  const [gridData, setGridData] = useState([]);
  const [masterGridData, setMasterGridData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [activeCard, setActiveCard] = useState('allCases');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState();
  const [reviewers, setReviewers] = useState([]);
  const gridRef = useRef(null);
  const chartSourceData = useRef([]);
  const isUnassignedQueueItem = (item) => item.statusName === REQUEST_STATUSES.PENDING_REVIEW;

  const fetchGridData = async () => {
    try {
      const data = await getAllLenderRequests();
      if (Array.isArray(data)) {
        const enriched = enrichWithDecision(data);
        // isDraftRequest checks externalStatus + statusName case-insensitively.
        // The PS gridRequestFeeds endpoint may return statusName/externalStatus as
        // 'Draft', '' or other values — the helper handles all cases.
        const nonDraft = enriched.filter(item => !isDraftRequest(item));
        setGridData(nonDraft);
        setMasterGridData(nonDraft);
        chartSourceData.current = nonDraft;
        setChartData(generateChartDataFromGrid(nonDraft));
        setLastUpdated(new Date().toLocaleString());
        setActiveCard('allCases');
        // Clear AG Grid column filters and reset pagination on refresh
        if (gridRef.current?.api) {
          gridRef.current.api.setFilterModel(null);
          gridRef.current.api.paginationGoToFirstPage();
        }
      }
    } catch (error) {
      console.error('Error fetching grid data:', error);
    }
  };

  // Helper function to apply card filtering logic
  const applyCardFilter = (data, cardType) => {
    if (cardType === 'allCases') {
      // All Cases = sum of unassigned + new + under review + in progress + escalated cases
      return data.filter(item => OPEN_STATUSES.includes(item.statusName));
    } else if (cardType === 'unassignedCases') {
      return data.filter(item => isUnassignedQueueItem(item));
    } else if (cardType === 'newCases') {
      return data.filter(item => item.statusName === REQUEST_STATUSES.NEW);
    } else if (cardType === 'inProgressCases') {
      return data.filter(item => IN_PROGRESS_STATUSES.includes(item.statusName));
    } else if (cardType === 'underReviewCases') {
      return data.filter(item => item.statusName === REQUEST_STATUSES.UNDER_REVIEW);
    } else if (cardType === 'completedCases') {
      return data.filter(item => CLOSED_STATUSES.includes(item.statusName) && item.isCompletedToday);
    } else if (cardType === 'escalatedCases') {
      return data.filter(item => item.statusName === REQUEST_STATUSES.ESCALATED);
    } else {
      return data;
    }
  };

  // Targeted row update for assignments without affecting filters
  const updateAssignedRow = async (requestId, newAssignedTo, newAssignedToName) => {
    try {
      if (gridRef.current?.api) {
        // Find the row that needs updating
        const rowNode = gridRef.current.api.getRowNode(requestId);
        if (rowNode) {
          // Update the row data with new assignment info
          const updatedData = {
            ...rowNode.data,
            assignedToLdapId: newAssignedTo,
            assignedToName: newAssignedToName
          };
          
          // Use transaction API to update just this row
          gridRef.current.api.applyTransaction({ update: [updatedData] });
        } else {
          // If row not found, do a minimal refresh for this specific case
          const data = await getAllLenderRequests();
          if (Array.isArray(data)) {
            const enriched = enrichWithDecision(data);
            const updatedRow = enriched.find(item => item.requestId === requestId);
            if (updatedRow) {
              gridRef.current.api.applyTransaction({ update: [updatedRow] });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating assigned row:', error);
    }
  };

  useEffect(() => {
    fetchGridData();
    
    // Fetch reviewers for assignment modal
    const loadReviewers = async () => {
      try {
        const reviewerList = await fetchReviewers();
        setReviewers(reviewerList);
      } catch (error) {
        console.error('Error fetching reviewers:', error);
      }
    };
    loadReviewers();
  }, []);

  // Direct assignment handler for LenderDashboard that preserves all filters
  const handleAssignment = async (selectedReviewer, notes, escalate, notesExternal) => {
    if (!selectedRequest || (!selectedReviewer && !escalate)) return false;

    try {
      const passData = JSON.parse(sessionStorage.getItem("passData"));
      const request = {
        requestStatusId: selectedRequest.projectRequestStatusId,
        requestMasterTrackerId: selectedRequest.requestId,
        assignTo: selectedReviewer ? selectedReviewer.userLdapId : null,
        notes: notes,
        isEscalated: escalate,
        notesIsExternal: notesExternal,
        createdBy: passData.user,
        updatedBy: passData.user,
      };

      const { assignReviewerToRequest } = await import("../shared/services/LenderRequestService");
      const status = await assignReviewerToRequest(request);

      if (status && gridRef.current?.api) {
        // Update only the specific row without any refresh
        const rowNode = gridRef.current.api.getRowNode(selectedRequest.requestId);
        if (rowNode) {
          const updatedData = {
            ...rowNode.data,
            assignedToLdapId: selectedReviewer ? selectedReviewer.userLdapId : null,
            assignedToName: selectedReviewer ? selectedReviewer.userName : null,
            ...(escalate ? { statusName: REQUEST_STATUSES.ESCALATED } : {})
          };
          gridRef.current.api.applyTransaction({ update: [updatedData] });
        }
      }

      return status;
    } catch (error) {
      console.error('Assignment error:', error);
      return false;
    }
  };

  const COMPLETED_STATUSES = CLOSED_STATUSES;

  // Helper function to check if a request was completed today based on item.statusName and item.latestStatusChangeDate
  const isCompletedToday = (item) => {
    if (!COMPLETED_STATUSES.includes(item.statusName)) return false;

    const dateToCheck = item.latestStatusChangeDate;
    if (!dateToCheck) return false;

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    const statusDate = new Date(dateToCheck);

    return statusDate >= startOfToday && statusDate <= endOfToday;
  };

  const enrichWithDecision = (data) => data.map(item => ({
    ...item,
    requestStatusDate: item.latestStatusChangeDate || item.requestStatusDate || null,
    slaColor: item.slaColor || null,
    hasDecision: COMPLETED_STATUSES.includes(item.statusName),
    isCompletedToday: isCompletedToday(item)
  }));

  // Generate chart data from grid data (used when cards filter the view)
  const generateChartDataFromGrid = (data) => {
    const statusGroups = {
      'In Progress Cases': IN_PROGRESS_STATUSES,
      'Escalated': [REQUEST_STATUSES.ESCALATED],
      'New Cases': [REQUEST_STATUSES.NEW],
      'Completed': CLOSED_STATUSES,
      'Unassigned': [REQUEST_STATUSES.PENDING_REVIEW],
    };

    const userMap = {};
    data.forEach(item => {
      const userName = item.assignedToName || "Unassigned";
      if (!userMap[userName]) {
        userMap[userName] = { 'In Progress Cases': 0, 'Escalated': 0, 'New Cases': 0, 'Completed': 0, 'Unassigned': 0 };
      }
      Object.entries(statusGroups).forEach(([group, statuses]) => {
        if (statuses.includes(item.statusName)) {
          if (group === 'Completed') {
            if (item.isCompletedToday) {
              userMap[userName][group]++;
            }
          } else {
            userMap[userName][group]++;
          }
        }
      });
    });

    // Filter out users with zero data across all groups and sort alphabetically with Unassigned at end
    const allLabels = Object.keys(userMap);
    const filteredLabels = allLabels.filter(user =>
      Object.values(userMap[user]).some(count => count > 0)
    );
    
    // Sort labels alphabetically with "Unassigned" at the end
    const labels = filteredLabels.sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    
    const colors = {
      'In Progress Cases': { bg: 'rgba(21, 101, 192, 0.6)', border: 'rgba(21, 101, 192, 1)' }, // Blue theme
      'Escalated': { bg: 'rgba(249, 168, 37, 0.6)', border: 'rgba(249, 168, 37, 1)' }, // Yellow theme
      'New Cases': { bg: 'rgba(230, 81, 0, 0.6)', border: 'rgba(230, 81, 0, 1)' },
      'Completed': { bg: 'rgba(46, 125, 50, 0.6)', border: 'rgba(46, 125, 50, 1)' },
      'Unassigned': { bg: 'rgba(183, 28, 28, 0.6)', border: 'rgba(183, 28, 28, 1)' },
    };

    const datasets = Object.keys(statusGroups).map(group => ({
      label: group,
      data: labels.map(user => userMap[user][group]),
      backgroundColor: colors[group].bg,
      borderColor: colors[group].border,
      borderWidth: 1,
      borderRadius: 5,
      barPercentage: 0.8,
      categoryPercentage: 0.8,
    }));

    return { labels, datasets };
  };

  const handleCardClick = async (type) => {
    setActiveCard(type);
    // Re-fetch data from API on every card click
    try {
      const freshData = await getAllLenderRequests();
      if (Array.isArray(freshData)) {
        const enriched = enrichWithDecision(freshData);
        const nonDraft = enriched.filter(item => !isDraftRequest(item));
        setMasterGridData(nonDraft);

        // Use the helper function for consistent filtering
        const filteredData = applyCardFilter(nonDraft, type);

        setGridData(filteredData);
        // Chart reflects the card-filtered data
        chartSourceData.current = filteredData;
        setChartData(generateChartDataFromGrid(filteredData));
        setLastUpdated(new Date().toLocaleString());
        // Clear AG Grid column filters and reset pagination on card click
        if (gridRef.current?.api) {
          gridRef.current.api.setFilterModel(null);
          gridRef.current.api.paginationGoToFirstPage();
        }
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleChartClick = (event, elements) => {
    if (elements?.length > 0 && chartData) {
      const { labels, datasets } = chartData;

      if (labels && datasets) {
        const clickedElement = elements[0];
        const clickedBarIndex = clickedElement.index;
        const clickedDatasetIndex = clickedElement.datasetIndex;

        const clickedLabel = labels[clickedBarIndex];
        const clickedStatus = datasets[clickedDatasetIndex]?.label;

        const statusMapping = {
          'In Progress Cases': IN_PROGRESS_STATUSES,
          'Escalated': [REQUEST_STATUSES.ESCALATED],
          'New Cases': [REQUEST_STATUSES.NEW],
          'Completed': CLOSED_STATUSES,
          'Unassigned': [REQUEST_STATUSES.PENDING_REVIEW]
        };

        // Filter from chartSourceData (same dataset that generated the chart) so counts match
        const filteredData = chartSourceData.current.filter(item => {
          const matchesUser = clickedLabel === 'Unassigned'
            ? isUnassignedQueueItem(item)
            : (item.assignedToLdapId === clickedLabel || item.assignedToName === clickedLabel);
          const matchesStatus = statusMapping[clickedStatus]?.includes(item.statusName);
          // For Completed, also enforce isCompletedToday to match chart generation logic
          if (clickedStatus === 'Completed' && matchesStatus) {
            return matchesUser && item.isCompletedToday;
          }
          return matchesUser && matchesStatus;
        });

        setGridData(filteredData);
      }
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'CPM Open Cases by User' },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: 'Number of Cases' },
        ticks: {
          font: { size: 12 },
          stepSize: 1,
          callback: function(value) {
            if (Number.isInteger(value)) return value;
            return null;
          }
        },
      },
      y: {
        stacked: true,
        position: 'left',
        title: { display: true, text: 'Users' },
        ticks: {
          font: { size: 11 },
          autoSkip: false,
          padding: 8,
          callback: function(value) {
            const label = this.getLabelForValue(value);
            return label.length > 20 ? label.substring(0, 18) + '...' : label;
          }
        },
      },
    },
    data: chartData || { labels: [], datasets: [] },
  };

  const chartHeight = 450;

  const underReviewCount = masterGridData.filter(item => item.statusName === REQUEST_STATUSES.NEW).length;

  return (
    <div className="dashboard-container" data-testid="lender-dashboard">{/* Test ID for background refresh detection */}
        <div className="dashboard-header">
            <div className="refresh-section">
                <span style={{ color: 'var(--theme-blue)' }}>Last updated: {lastUpdated}</span>
                <RefreshIcon className="refresh-icon" onClick={fetchGridData} />
            </div>
        </div>
        {masterGridData.length > 0 ? (
            <>
                <div className="row">
                    {(() => {
                        const allCases = masterGridData.filter(item => OPEN_STATUSES.includes(item.statusName)).length;
                      const unassignedCases = masterGridData.filter(item => isUnassignedQueueItem(item)).length;
                        const inProgressCases = masterGridData.filter(item => IN_PROGRESS_STATUSES.includes(item.statusName)).length;
                        const completedCases = masterGridData.filter(item => CLOSED_STATUSES.includes(item.statusName) && item.isCompletedToday).length;
                        // Count escalated cases
                        const escalatedCases = masterGridData.filter(item => item.statusName === REQUEST_STATUSES.ESCALATED).length;
                        return (
                            <>
                                <div className={`card all-cases${activeCard === 'allCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('allCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('allCases'); }}} style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ color: 'black', textAlign: 'center', paddingTop: '10px' }}>All Active Cases</h3>
                                        <p style={{ color: 'black', textAlign: 'center' }}>{allCases}</p>
                                    </div>
                                </div>
                                <div className={`card unassigned-cases${activeCard === 'unassignedCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('unassignedCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('unassignedCases'); }}} style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ textAlign: 'center', paddingTop: '10px' }}>Unassigned Cases</h3>
                                        <p style={{ textAlign: 'center' }}>{unassignedCases}</p>
                                    </div>
                                </div>
                                <div className={`card new-cases${activeCard === 'newCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('newCases')} role="button" tabIndex={0} aria-label={`New Cases: ${underReviewCount}`} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('newCases'); }}} style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ textAlign: 'center', paddingTop: '10px' }}>New Cases</h3>
                                        <p style={{ textAlign: 'center' }}>{underReviewCount}</p>
                                    </div>
                                </div>
                                <div className={`card in-progress-cases${activeCard === 'inProgressCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('inProgressCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('inProgressCases'); }}} style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ textAlign: 'center', paddingTop: '10px' }}>In Progress Cases</h3>
                                        <p style={{ textAlign: 'center' }}>{inProgressCases}</p>
                                    </div>
                                </div>
                                <div className={`card completed-cases${activeCard === 'completedCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('completedCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('completedCases'); }}} style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <div>
                                    <h3 style={{ textAlign: 'center', paddingTop: '0px', marginBottom: '0px' }}>Completed Cases</h3>
                                    <p style={{ textAlign: 'center', marginBottom: '0px' }}>{completedCases}</p>
                                    <small style={{ textAlign: 'center', display: 'block', color: '#2e7d32', fontSize: '10px', fontWeight: 'bold', marginTop: '0px' }}>
                                      Today
                                    </small>
                                  </div>
                                </div>
                                <div className={`card escalated-cases${activeCard === 'escalatedCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('escalatedCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('escalatedCases'); }}} style={{ height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ textAlign: 'center', paddingTop: '10px' }}>Escalated Cases</h3>
                                        <p style={{ textAlign: 'center' }}>{escalatedCases}</p>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>

                <div className="row" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                    <div className="chart-container" style={{ height: chartHeight, width: '40%' }}>
                        {chartData ? (
                            <Bar
                                key={JSON.stringify(chartData)}
                                data={chartData}
                                options={{
                                  ...chartOptions,
                                  onClick: (event, elements) => handleChartClick(event, elements),
                                }}
                            />
                        ) : (
                            <p>Loading chart data...</p>
                        )}
                    </div>
                    <div className="dashboard-grid" style={{ height: 450, width: '60%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={gridData}
                            getRowId={(params) => params.data.requestId}
                            columnDefs={[
                                { headerName: 'Project ID', field: 'projectId', minWidth: 100, maxWidth: 115, suppressMenu: false, floatingFilter: false, menuTabs: ['filterMenuTab'], filter: 'agTextColumnFilter', filterParams: { buttons: ['reset'] } },
                                { 
                                  headerName: 'Type', 
                                  field: 'requestTypeName',
                                  valueFormatter: (params) => {
                                    if (!params.value) return '';
                                    return params.value
                                      .replace(/\bStatus\b/gi, '')
                                      .replace(/\bLender\b/gi, '')
                                      .replace(/\s{2,}/g, ' ')
                                      .trim();
                                  },
                                  filter: 'agSetColumnFilter',
                                  filterParams: {
                                    suppressSelectAll: false,
                                    suppressMiniFilter: false,
                                    suppressSorting: true,
                                    textFormatter: (value) => value,
                                    buttons: ['reset'],
                                    closeOnApply: true,
                                    suppressRemoveEntries: true
                                  },
                                  menuTabs: ['filterMenuTab'],
                                  suppressMenu: false,
                                  floatingFilter: false
                                },
                                { headerName: 'Sub Types', field: 'requestSubTypeName', minWidth: 280,
                                  filter: 'agTextColumnFilter',
                                  filterParams: {
                                    buttons: ['reset']
                                  },
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  cellRenderer: SubTypeTooltipRenderer,
                                  valueGetter: (params) => {
                                    const reasonCodes = params.data?.reasonCodes;
                                    if (reasonCodes && reasonCodes.length > 0) {
                                      return reasonCodes.map(rc => rc.reasonCode).filter(Boolean).join(', ');
                                    }
                                    const docs = params.data?.requestFeedDocumentDTOList;
                                    if (docs && docs.length > 0) {
                                      const uniqueSubTypes = [...new Set(docs.map(d => d.requestSubTypeName).filter(Boolean))];
                                      if (uniqueSubTypes.length > 0) return uniqueSubTypes.join(', ');
                                    }
                                    return params.data?.requestSubTypeName || '';
                                  }
                                },
                                {
                                  headerName: '',
                                  field: 'statusName',
                                  headerTooltip: 'Escalation Indicator',
                                  maxWidth: 40,
                                  cellRenderer: EscalatedIndicatorRenderer,
                                  sortable: false,
                                  filter: false,
                                  resizable: false,
                                  suppressMenu: true,
                                  floatingFilter: false
                                },
                                {
                                  headerName: 'Req. Status',
                                  field: 'internalStatus',
                                  filter: 'agSetColumnFilter',
                                  filterParams: {
                                    suppressSelectAll: false,
                                    suppressMiniFilter: false,
                                    suppressSorting: true,
                                    textFormatter: (value) => value,
                                    buttons: ['reset'],
                                    closeOnApply: true,
                                    suppressRemoveEntries: true
                                  },
                                  menuTabs: ['filterMenuTab'],
                                  suppressMenu: false,
                                  floatingFilter: false
                                },
                                { 
                                  headerName: 'Assigned To', 
                                  field: 'assignedToName', 
                                  minWidth: 180,
                                  filter: 'agSetColumnFilter',
                                  filterParams: {
                                    suppressSelectAll: false,
                                    suppressMiniFilter: false,
                                    suppressSorting: true,
                                    textFormatter: (value) => value || 'Unassigned',
                                    buttons: ['reset'],
                                    closeOnApply: true,
                                    suppressRemoveEntries: true
                                  },
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  cellRenderer: EditableCellRenderer,
                                  cellRendererParams: {
                                    onDataRefresh: () => {
                                      // NO-OP: Prevent any refresh during assignments
                                      // LenderDashboard handles updates via direct assignment handler
                                    },
                                    openModal: (request) => {
                                      setSelectedRequest(request);
                                      setShowAssignModal(true);
                                    }
                                  }
                                },
                                {
                                  headerName: 'Prev. Worked By',
                                  field: 'previouslyWorkedByName',
                                  minWidth: 160,
                                  filter: 'agSetColumnFilter',
                                  filterParams: { buttons: ['reset'] },
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  valueFormatter: (params) => {
                                    const prevWorkedBy = params.value;
                                    const assignedTo = params.data?.assignedToName;
                                    if (!prevWorkedBy || prevWorkedBy === assignedTo) return '\u2014';
                                    return prevWorkedBy;
                                  }
                                },
                                {
                                  headerName: 'Status Date',
                                  field: 'requestStatusDate',
                                  minWidth: 150,
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  filter: 'agDateColumnFilter',
                                  filterParams: { buttons: ['reset'] },
                                  valueFormatter: (params) => {
                                    if (!params.value) return '\u2014';
                                    const date = new Date(params.value);
                                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                                    const dd = String(date.getDate()).padStart(2, '0');
                                    const yyyy = date.getFullYear();
                                    const hh = String(date.getHours()).padStart(2, '0');
                                    const min = String(date.getMinutes()).padStart(2, '0');
                                    return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
                                  }
                                },
                                { headerName: 'Req. Date', field: 'requestCreatedDate', cellRenderer: RequestDateRenderer, minWidth: 150, maxWidth: 170, suppressMenu: false, floatingFilter: false, filter: 'agDateColumnFilter', filterParams: { buttons: ['reset'], suppressAndOrCondition: true, comparator: (filterLocalDateAtMidnight, cellValue) => { const cellDate = new Date(cellValue); const filterDate = new Date(filterLocalDateAtMidnight); if (cellDate < filterDate) return -1; if (cellDate > filterDate) return 1; return 0; } }, menuTabs: ['filterMenuTab'] },
                            ]}
                            defaultColDef={{ sortable: true, filter: true, floatingFilter: false, resizable: true }}
                            tooltipShowDelay={300}
                            tooltipHideDelay={0}
                            tooltipMouseTrack={true}
                            enableBrowserTooltips={false}
                            pagination={true}
                            paginationPageSize={20}
                            paginationPageSizeSelector={[20, 50, 100]}
                            getRowStyle={(params) => {
                              const status = params.data?.statusName || params.data?.internalStatus;
                              const isEscalated = status === REQUEST_STATUSES.ESCALATED || params.data?.isEscalated === true || (params.data?.requestFeedActivityTrackerDTOList || []).some(a => a?.isEscalated === true);
                              if (isEscalated) return { background: '#fff9c4' };
                              return null;
                            }}
                        />
                    </div>
                </div>
            </>
        ) : (
            <p>Loading dashboard data...</p>
        )}
        
        {/* Assignment Modal - Custom for LenderDashboard to prevent any refresh */}
        <AssignReviewerModal
            selectedRequest={selectedRequest}
            show={showAssignModal}
            setShow={setShowAssignModal}
            reviewers={reviewers}
            customHandler={handleAssignment}
            source="lender-dashboard"
        />
    </div>
);
};

export default LenderDashboard;